import { findBand, BAND_COLORS } from "../bands";

// FRAX — 10-year fracture probability. The FRAX algorithm is proprietary to the
// University of Sheffield and calibrated per country, so it is NOT computed
// here: this calculator records the result from the official tool so it saves
// on the visit and prints with the prescription. The outcome (major
// osteoporotic vs hip) is an option, i.e. a score variant — both can be saved
// on the same visit and both print, like DAS28's ESR/CRP.
// ⚠️ Band thresholds are guideline-informed (NOGG / US NOF); keep a clinician
// in the loop before relying on them clinically.
const majorBands = [
    { label: "Low", color: BAND_COLORS.low, lt: 10 },
    { label: "Moderate", color: BAND_COLORS.moderate, lt: 20 },
    { label: "High", color: BAND_COLORS.high },
];

// US NOF treatment threshold for hip fracture probability: ≥3%.
const hipBands = [
    { label: "Low", color: BAND_COLORS.low, lt: 3 },
    { label: "High", color: BAND_COLORS.high },
];

const bandsFor = (o) => (o?.outcome === "Hip" ? hipBands : majorBands);

export const frax = {
    id: "frax",
    name: "FRAX",
    subtitle: "10-year fracture probability (official FRAX result)",
    group: "Risk assessment",
    status: "ready",
    layout: "stacked",
    intro:
        "Compute on the official FRAX tool (frax.shef.ac.uk) and record the 10-year probability here — the FRAX algorithm is proprietary and country-calibrated, so it cannot be computed in-app.",
    options: [
        { key: "outcome", label: "Outcome", choices: ["Major osteoporotic", "Hip"], default: "Major osteoporotic" },
    ],
    fields: [
        {
            key: "probability",
            label: (o) =>
                o.outcome === "Hip"
                    ? "10-year probability of hip fracture"
                    : "10-year probability of major osteoporotic fracture",
            unit: "%",
            type: "number",
            min: 0,
            max: 100,
            step: 0.1,
        },
    ],
    compute: (v) => v.probability,
    interpret: (score, o) => findBand(score, bandsFor(o)),
    bands: (o) => bandsFor(o),
    about:
        "FRAX estimates the 10-year probability of a major osteoporotic fracture (clinical spine, hip, forearm, or humerus) and of hip fracture, from clinical risk factors — age, sex, BMI, prior fracture, parental hip fracture, smoking, glucocorticoids, rheumatoid arthritis, secondary osteoporosis, alcohol — optionally refined with femoral neck bone mineral density. Compute it on the official country-specific tool and record the result here. Bands shown are guideline-informed: major osteoporotic ≥20% and hip ≥3% are widely used treatment thresholds (US NOF); local guidance may differ.",
    references: [
        "Kanis JA, et al. FRAX and the assessment of fracture probability in men and women from the UK. Osteoporos Int. 2008.",
        "Cosman F, et al. Clinician's guide to prevention and treatment of osteoporosis. Osteoporos Int. 2014.",
    ],
};
